// Shared Input - With Radix Label integration
import { forwardRef } from 'react'
import * as Label from '@radix-ui/react-label'
import clsx from 'clsx'

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
  /** Reserve space for error message to prevent layout shift */
  reserveErrorSpace?: boolean
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, hint, leftIcon, rightIcon, reserveErrorSpace, id, ...props }, ref) => {
    const inputId = id || props.name || 'input'

    return (
      <div className="w-full">
        {label && (
          <Label.Root
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            {label}
          </Label.Root>
        )}

        <div className="relative">
          {leftIcon && (
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              {leftIcon}
            </div>
          )}

          <input
            ref={ref}
            id={inputId}
            // Marks the field invalid to screen readers, not just visually.
            // Declared before the spread so a caller can still override it.
            aria-invalid={error ? true : undefined}
            className={clsx(
              // Base styles
              'w-full h-14 px-4 text-base text-gray-900 bg-white',
              'border border-gray-300 rounded-xl',
              'placeholder:text-gray-400',
              // Focus states using brand colors
              'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
              // Disabled state
              'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
              // Error state
              error && 'border-red-500 focus:ring-red-500 focus:border-red-500',
              // Icon padding
              leftIcon && 'pl-12',
              rightIcon && 'pr-12',
              className
            )}
            {...props}
          />

          {rightIcon && (
            <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
              {rightIcon}
            </div>
          )}
        </div>

        {/* Reserve space for error/hint to prevent layout shift */}
        {reserveErrorSpace ? (
          <div className="mt-2 min-h-[1.25rem]">
            {error && <p className="text-sm text-red-600">{error}</p>}
            {hint && !error && <p className="text-xs text-gray-500">{hint}</p>}
          </div>
        ) : (
          <>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
            {hint && !error && <p className="mt-2 text-xs text-gray-500">{hint}</p>}
          </>
        )}
      </div>
    )
  }
)

Input.displayName = 'Input'
